import { useEffect, useMemo, useRef, useState } from 'react';
import { freshCanvas } from '../scene/useScene';
import { useTight } from '../../components/useTight';
import { Link, useNavigate } from 'react-router-dom';
import RowMenu from '../../components/RowMenu';
import { KINDS } from '../../concepts/kinds';
import { isKnownKind, type ProgramTrack, type ProgramView } from '../../programs/types';
import { AddProgramsMenu } from '../../pages/home/shared';
import { ProgramMenuItems } from '../../pages/home/ClassicHome';
import type { HomeModel } from '../../pages/home/useHome';
import type { BookSpec, PlayroomHandle } from './Playroom';

const CATEGORY_GLYPH: Record<string, string> = {
  Receptive: '👂', Expressive: '🗣️', Echoic: '🔁', Intraverbal: '💬', Mand: '🙋',
  Matching: '🧩', Sorting: '🗂️', Sequencing: '🔢', Math: '➕', Literacy: '🔤',
  'Motor imitation': '🤸', Social: '🤝', 'Daily living': '🪥',
};
const COVERS = ['#3d5a80', '#e07a5f', '#81b29a', '#6d597a', '#f2a541', '#2a9d8f', '#bc4749', '#5e60ce', '#c97b3a', '#457b9d'];

function glyphOf(p: ProgramView): string {
  const text = JSON.stringify(p.config ?? {});
  const m = text.match(/\p{Extended_Pictographic}(️|‍\p{Extended_Pictographic})*/u);
  return m ? m[0] : CATEGORY_GLYPH[p.category ?? ''] ?? '📘';
}

function colourOf(p: ProgramView, i: number): string {
  const c = p.category ?? '';
  let h = 0;
  for (const ch of c) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return COVERS[(c ? h : i) % COVERS.length];
}

function motionAllowed(reduceMotion: boolean) {
  return !reduceMotion && window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
}

export default function PlayroomHome({ h }: { h: HomeModel }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const handleRef = useRef<PlayroomHandle | null>(null);
  const topRef = useRef<HTMLDivElement | null>(null);
  const tight = useTight(topRef);
  const nav = useNavigate();
  const [failed, setFailed] = useState(false);
  const [track, setTrack] = useState<ProgramTrack>('CURRENT');
  const [first, setFirst] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [perPage, setPerPage] = useState(window.innerWidth < 720 ? 1 : 3);
  const moving = motionAllowed(h.settings.reduceMotion);
  const list = track === 'CURRENT' ? h.current : h.maintenance;

  const books: BookSpec[] = useMemo(() => list.map((p, i) => ({
    id: p.id,
    title: p.name,
    subtitle: p.category ?? (isKnownKind(p.kind) ? KINDS[p.kind].label : 'Program'),
    glyph: glyphOf(p),
    colour: colourOf(p, i),
  })), [list]);

  const latest = useRef({ books, first, selected });
  latest.current = { books, first, selected };

  useEffect(() => {
    const onResize = () => setPerPage(window.innerWidth < 720 ? 1 : 3);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const placeholder = canvasRef.current;
    if (!placeholder || failed) return;
    let cancelled = false;
    const root = document.documentElement;
    const { canvas, restore } = freshCanvas(placeholder);
    void import('./Playroom').then(({ mountPlayroom }) => {
      if (cancelled) return;
      try {
        handleRef.current = mountPlayroom(canvas, motionAllowed(h.settings.reduceMotion), {
          onPick: (id) => setSelected((cur) => (cur === id ? cur : id)),
          onLost: () => { if (!cancelled) setFailed(true); },
        });
        handleRef.current.setBooks(latest.current.books);
        handleRef.current.setIndex(latest.current.first);
        handleRef.current.setSelected(latest.current.selected);
        root.setAttribute('data-room', 'on');
      } catch {
        setFailed(true);
      }
    });
    return () => {
      cancelled = true;
      handleRef.current?.dispose();
      handleRef.current = null;
      restore();
      root.removeAttribute('data-room');
    };
  }, [failed]);

  useEffect(() => { handleRef.current?.setBooks(books); }, [books, failed]);
  useEffect(() => { handleRef.current?.setIndex(first); }, [first, books]);
  useEffect(() => { handleRef.current?.setSelected(selected); }, [selected]);
  useEffect(() => { handleRef.current?.setMoving(moving); }, [moving]);
  useEffect(() => { setFirst(0); setSelected(null); }, [track, h.categoryFilter, h.kindFilter]);
  useEffect(() => {
    if (first > Math.max(0, list.length - 1)) setFirst(Math.max(0, list.length - perPage));
  }, [list.length, first, perPage]);

  const shown = list.slice(first, first + perPage);
  const canBack = first > 0;
  const canNext = first + perPage < list.length;
  const playUrl = (p: ProgramView) => `/play/${p.id}${h.learnerId ? `?learner=${encodeURIComponent(h.learnerId)}` : ''}`;
  const chosen = list.find((p) => p.id === selected) ?? null;

  return (
    <div className="room">
      {!failed && <canvas ref={canvasRef} className="room-canvas" aria-hidden="true" />}

      {h.error && <div className="error room-error" role="alert">{h.error}</div>}

      <div className="room-bottom">
        <div ref={topRef} className={tight ? 'room-top is-tight' : 'room-top'}>
          <div className="room-chips" role="tablist" aria-label="Which programs">
            {(['CURRENT', 'MAINTENANCE'] as const).map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={track === t}
                className={track === t ? 'room-chip is-active' : 'room-chip'}
                onClick={() => setTrack(t)}
              >
                {t === 'CURRENT' ? 'Learning now' : 'Learned'}
                <span className="room-count">{t === 'CURRENT' ? h.current.length : h.maintenance.length}</span>
              </button>
            ))}
            {h.categories.length > 0 && (
              <select
                className="room-select"
                aria-label="Category"
                value={h.categoryFilter}
                onChange={(e) => h.setCategoryFilter(e.target.value)}
              >
                <option value="">All categories</option>
                {h.categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            {h.kinds.length > 1 && (
              <select className="room-select" aria-label="Type of game" value={h.kindFilter} onChange={(e) => { h.setKindFilter(e.target.value); }}>
                <option value="">All types of game</option>
                {h.kinds.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
              </select>
            )}
          </div>
          <div className="room-actions">
            <AddProgramsMenu h={h} />
          </div>
        </div>
        {chosen ? (
          <div className="room-chosen" role="region" aria-label={`Chosen: ${chosen.name}`}>
            <span className="room-chosen-name">{chosen.name}</span>
            <Link className="btn" to={playUrl(chosen)}>Play</Link>
            <button type="button" className="btn secondary" onClick={() => setSelected(null)}>Put back</button>
          </div>
        ) : (
          <p className="room-title">
            {h.loading ? 'Loading…' : list.length === 0
              ? (track === 'CURRENT' ? 'No programs yet — add one to fill the shelf' : 'Learned programs come here')
              : 'Pick a program'}
          </p>
        )}

        {list.length > 0 && (
          <div className="room-row">
            <button
              type="button"
              className="room-arrow"
              aria-label="Previous programs"
              disabled={!canBack}
              onClick={() => setFirst((f) => Math.max(0, f - perPage))}
            >
              ‹
            </button>
            <ul className="room-cards">
              {shown.map((p) => (
                <li key={p.id} className={selected === p.id ? 'room-card is-chosen' : 'room-card'}>
                  <button type="button" className="room-card-pick" onClick={() => setSelected(p.id)}>
                    <span className="room-card-glyph" aria-hidden="true">{glyphOf(p)}</span>
                    <span className="room-card-name">{p.name}</span>
                    <span className="room-card-meta">
                      {isKnownKind(p.kind) ? KINDS[p.kind].label : p.kind}
                      {h.dueIds.has(p.id) ? ' · ready for a check' : ''}
                    </span>
                  </button>
                  <div className="room-card-actions">
                    <button type="button" className="btn" onClick={() => nav(playUrl(p))}>Play</button>
                    <RowMenu label={`More for ${p.name}`}>
                      <ProgramMenuItems h={h} p={p} />
                    </RowMenu>
                  </div>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="room-arrow"
              aria-label="Next programs"
              disabled={!canNext}
              onClick={() => setFirst((f) => Math.min(Math.max(0, list.length - perPage), f + perPage))}
            >
              ›
            </button>
          </div>
        )}
        {list.length > perPage && (
          <div className="room-dots" aria-hidden="true">
            {Array.from({ length: Math.ceil(list.length / perPage) }, (_, i) => (
              <span key={i} className={Math.floor(first / perPage) === i ? 'is-on' : ''} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
